import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PayrollHeader from '../../components/hris/payroll/PayrollHeader';
import PayrollStats from '../../components/hris/payroll/PayrollStats';
import PayrollTable from '../../components/hris/payroll/PayrollTable';
import { PayrollDetailModal } from '../../components/hris/payroll/PayrollDetailModal';
import { PayrollSlipModal } from '../../components/hris/payroll/PayrollSlipModal';
import GeneratePayrollDialog from '../../components/hris/payroll/GeneratePayrollDialog';
import { containerVariants, itemVariants } from '../../utils/animations';
import { usePayrollData } from '../../components/hris/payroll/hooks/usePayrollData';
import { usePayrollGenerator } from '../../components/hris/payroll/hooks/usePayrollGenerator';
import { usePayrollActions } from '../../components/hris/payroll/hooks/usePayrollActions';

export default function PayrollList() {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [detailModal, setDetailModal] = useState({ open: false, payroll: null, employee: null });
  const [slipModal, setSlipModal] = useState({ open: false, payroll: null, employee: null });
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  const { payrolls, employees, positions, loading, reload } = usePayrollData(
    selectedMonth,
    selectedYear
  );
  const { generating, generatePayroll } = usePayrollGenerator(selectedMonth, selectedYear, reload);
  const { approvePayroll, processPayment } = usePayrollActions(reload);

  const handleGenerateClick = () => {
    setGenerateDialogOpen(true);
  };

  const handleConfirmGenerate = async () => {
    setGenerateDialogOpen(false);
    await generatePayroll();
  };

  // Helper functions
  const getEmployeeName = (empId: number) => {
    return employees.find(e => e.id === empId)?.full_name || '-';
  };

  const getEmployeeId = (empId: number) => {
    return employees.find(e => e.id === empId)?.employee_id || '-';
  };

  const getEmployee = (empId: number) => {
    return employees.find(e => e.id === empId);
  };

  const getPositionName = (posId: number | string) => {
    const id = typeof posId === 'string' ? parseInt(posId) : posId;
    return positions.find(p => p.id === id)?.name || '-';
  };

  // Calculate totals
  const totalBaseSalary = payrolls.reduce((sum, p) => sum + (parseFloat(p.base_salary) || 0), 0);
  const totalOvertime = payrolls.reduce((sum, p) => sum + (parseFloat(p.overtime_pay) || 0), 0);
  const totalKPIBonus = payrolls.reduce((sum, p) => sum + (parseFloat(p.kpi_bonus) || 0), 0);
  const totalPayroll = payrolls.reduce((sum, p) => sum + (parseFloat(p.net_salary) || 0), 0);
  const totalEmployees = payrolls.length;

  // Modal handlers
  const openDetailModal = (payroll: any) => {
    const emp = getEmployee(payroll.employee_id);
    setDetailModal({ open: true, payroll, employee: emp });
  };

  const openSlipModal = (payroll: any) => {
    const emp = getEmployee(payroll.employee_id);
    setSlipModal({ open: true, payroll, employee: emp });
  };

  const handleApprove = (id: number) => {
    const payroll = payrolls.find(p => p.id === id);
    if (payroll) {
      approvePayroll(id, payroll);
    }
  };

  const handlePayment = (id: number) => {
    const payroll = payrolls.find(p => p.id === id);
    if (payroll) {
      processPayment(id, payroll);
    }
  };

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <PayrollHeader
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        onGenerateClick={handleGenerateClick}
        isGenerating={generating}
      />

      {/* Stats Cards */}
      <PayrollStats
        totalBaseSalary={totalBaseSalary}
        totalOvertime={totalOvertime}
        totalKPIBonus={totalKPIBonus}
        totalPayroll={totalPayroll}
        totalEmployees={totalEmployees}
      />

      {/* Table */}
      <motion.div variants={itemVariants}>
        <PayrollTable
          payrolls={payrolls}
          loading={loading}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          employees={employees}
          positions={positions}
          onDetailClick={openDetailModal}
          onSlipClick={openSlipModal}
          onApproveClick={handleApprove}
          onPaymentClick={handlePayment}
          getEmployeeName={getEmployeeName}
          getEmployeeId={getEmployeeId}
          getPositionName={getPositionName}
        />
      </motion.div>

      {/* Detail Modal */}
      <PayrollDetailModal
        open={detailModal.open}
        onOpenChange={open =>
          !open && setDetailModal({ open: false, payroll: null, employee: null })
        }
        payroll={detailModal.payroll}
        employee={detailModal.employee}
        getPositionName={getPositionName}
      />

      {/* Slip Gaji Modal */}
      <PayrollSlipModal
        open={slipModal.open}
        onOpenChange={open => !open && setSlipModal({ open: false, payroll: null, employee: null })}
        payroll={slipModal.payroll}
        employee={slipModal.employee}
        getPositionName={getPositionName}
      />

      {/* Generate Payroll Dialog */}
      <GeneratePayrollDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onConfirm={handleConfirmGenerate}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        isGenerating={generating}
      />
    </motion.div>
  );
}
